'use client';

/**
 * ContentAnswerMount — the standalone form of ContentAnswerCard.
 *
 * mode10's FAQ grid hands ContentAnswerCard its `entry` as a prop (it already
 * loaded the whole `content_set`). When Ask Klyde mounts the answer in the
 * concertina / reception renderer-slot, there's no parent grid — the card is
 * mounted by `renderer_card="content_answer"` with only the resolved subject
 * (`entityId` = the matched node's `media_sid`). So this wrapper self-fetches
 * that ONE entry via `content_answer(key)` — `content_set` parameterised to a
 * single answer — and hands it to the same renderer. Identical mount contract
 * to any other entity-scoped card (MatrixCard → `rfq_matrix`): subject in,
 * card out.
 *
 * @see klyde_edge/ui_config/handlers/content.py — content_answer strategy
 */

import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import { cn } from '@/lib/utils/cn';
import { text } from '@/lib/design/tokens';
import { ContentAnswerCard } from './content-answer-card';
import type { ContentQuestion } from './use-content-set';

export function ContentAnswerMount({ entityId }: { entityId?: string }) {
  const strategy = useCapabilityStore((s) => s.strategyName('content', 'content_answer'));
  const lang = useLanguageStore((s) => s.lang);
  // Same query-key shape as useContentSet, just keyed to one entry — the
  // capture envelope then records this card's payload as that one answer,
  // not the whole tree.
  const params = useMemo(
    () => (entityId ? { key: entityId, ...(lang ? { lang } : {}) } : {}),
    [entityId, lang],
  );
  const queryKey = useMemo(() => ['strategy-data', strategy, params], [strategy, params]);
  const query = useStrategyData<ContentQuestion>(strategy, params, !!entityId);
  const entry = query.data?.data as ContentQuestion | undefined;

  if (!entityId) return null;
  if (!entry) {
    return (
      <div className={cn(text.muted, 'flex items-center gap-2 p-5')}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
      </div>
    );
  }
  return <ContentAnswerCard entry={entry} queryKey={queryKey} />;
}
